// Services venture maths — sell your time/skill. High margin (almost no cost) but
// capped by the hours you can work, which are limited by your energy. That cap is
// the lesson: the S quadrant (self-employed) has a ceiling — you can only sell so
// many hours of yourself.

import { priceFactor } from '../business/formulas.js';

export const SERVICE_SWEET_RATE = 15; // a fair hourly rate
export const SERVICE_BASE_DEMAND = 10; // hours of work clients want at the fair rate
export const ENERGY_PER_HOUR = 4; // energy each worked hour costs

/** The most hours you could work given your current energy. */
export function maxServiceHours(energy: number): number {
  return Math.max(0, Math.floor(Math.max(0, energy) / ENERGY_PER_HOUR));
}

/** How many hours of work clients want at this rate (supply & demand on time). */
export function serviceDemandHours(rate: number): number {
  return Math.max(0, Math.round(SERVICE_BASE_DEMAND * priceFactor(rate, SERVICE_SWEET_RATE)));
}

export type ServiceResult = {
  demandHours: number;
  maxHours: number;
  hoursWorked: number;
  revenue: number;
  energyCost: number;
};

/** Resolve a week of services given the rate, the hours the player planned, and
 *  their energy. Hours worked are capped by demand AND by energy. */
export function serviceWeeklyResult(args: { rate: number; plannedHours: number; energy: number }): ServiceResult {
  const demandHours = serviceDemandHours(args.rate);
  const maxHours = maxServiceHours(args.energy);
  const hoursWorked = Math.max(0, Math.min(args.plannedHours, demandHours, maxHours));
  return {
    demandHours,
    maxHours,
    hoursWorked,
    revenue: Math.round(hoursWorked * args.rate),
    energyCost: hoursWorked * ENERGY_PER_HOUR,
  };
}

/** The hard ceiling on weekly income at a given rate + energy (the S-quadrant
 *  lesson): rate × whichever is smaller, the hours wanted or the hours you can do. */
export function serviceCeiling(rate: number, energy: number): number {
  return Math.round(rate * Math.min(serviceDemandHours(rate), maxServiceHours(energy)));
}
